/**
 * Thin convenience wrappers around the API client, grouped by feature.
 * Components import these instead of poking `api.get(...)` directly so the
 * URL surface lives in one place — easier to grep, easier to mock.
 */
import api from "./client";

export const auth = {
  register: (data) => api.post("/auth/register/", data),
  login: (data) => api.post("/auth/login/", data),
  logout: () => api.post("/auth/logout/", {}),
  passwordReset: (email) => api.post("/auth/password-reset/", { email }),
  passwordResetConfirm: (data) => api.post("/auth/password-reset/confirm/", data),
  deleteAccount: (password) => api.post("/auth/account/delete/", { password }),
};

export const account = {
  tokens: () => api.get("/account/tokens/"),
  export: () => api.get("/account/export/"),
};

export const websites = {
  list: () => api.get("/websites/"),
  create: (data) => api.post("/websites/", data),
  remove: (id) => api.delete(`/websites/${id}/`),
};

// Phase 6: project sharing — invite registered users to collaborate
// on a website. Roles: "editor" (write access except delete/share) or
// "read_only" (dashboards + reports only). The owner is the only role
// that can manage these shares.
export const websiteShares = {
  list: (websiteId) => api.get(`/websites/${websiteId}/shares/`),
  invite: (websiteId, { email, role }) =>
    api.post(`/websites/${websiteId}/shares/`, { email, role }),
  remove: (websiteId, shareId) =>
    api.delete(`/websites/${websiteId}/shares/${shareId}/`),
};

export const regions = {
  // Public catalogue of region codes + English labels. The dashboard
  // localises the labels via i18n keys (`regions.<code>`) when available.
  list: () => api.get("/websites/regions/"),
};

export const competitors = {
  list: (websiteId) => api.get(`/websites/${websiteId}/competitors/`),
  create: (websiteId, data) =>
    api.post(`/websites/${websiteId}/competitors/`, data),
  remove: (websiteId, id) =>
    api.delete(`/websites/${websiteId}/competitors/${id}/`),
  report: (websiteId) =>
    api.get(`/websites/${websiteId}/competitors/report/`),
  mentions: (websiteId, id) =>
    api.get(`/websites/${websiteId}/competitors/${id}/mentions/`),
};

export const prompts = {
  list: (websiteId) => api.get(`/websites/${websiteId}/prompts/`),
  create: (websiteId, data) => api.post(`/websites/${websiteId}/prompts/`, data),
  update: (websiteId, id, data) =>
    api.patch(`/websites/${websiteId}/prompts/${id}/`, data),
  remove: (websiteId, id) => api.delete(`/websites/${websiteId}/prompts/${id}/`),
  run: (websiteId, id, ai_models) =>
    api.post(`/websites/${websiteId}/prompts/${id}/run/`, { ai_models }),
  // Phase 1: LLM-generated buyer-journey prompts tuned to the site's
  // detected niche + region. Returns `{ prompts: string[], niche, region }`.
  suggestions: (websiteId) =>
    api.get(`/websites/${websiteId}/prompts/suggestions/`),
  // Phase 3: dispatch a scan job per (active prompt × model). Server
  // returns `{ batch_size, prompts, ai_models, total_cost }`.
  runAll: (websiteId, ai_models) =>
    api.post(`/websites/${websiteId}/prompts/run-all/`, { ai_models }),
  // Phase 4: prompts where competitors are winning. Returns an array
  // of `{ prompt_id, prompt_text, competitors, brand_miss_rate, ... }`.
  discovery: (websiteId) =>
    api.get(`/websites/${websiteId}/prompts/discovery/`),
};

export const ga4 = {
  // Phase 4: server-side OAuth + Bot Traffic Analytics. The frontend
  // never sees the refresh/access tokens — only metadata about the
  // connection state.
  status: (websiteId) =>
    api.get(`/websites/${websiteId}/integrations/ga4/status/`),
  authorize: (websiteId) =>
    api.post(`/websites/${websiteId}/integrations/ga4/authorize/`, {}),
  properties: (websiteId) =>
    api.get(`/websites/${websiteId}/integrations/ga4/properties/`),
  connect: (websiteId, data) =>
    api.post(`/websites/${websiteId}/integrations/ga4/connect/`, data),
  disconnect: (websiteId) =>
    api.post(`/websites/${websiteId}/integrations/ga4/disconnect/`, {}),
  botTraffic: (websiteId, params = {}) =>
    api.get(`/websites/${websiteId}/integrations/ga4/bot-traffic/`, {
      params: {
        ...(params.start ? { start: params.start } : {}),
        ...(params.end ? { end: params.end } : {}),
        ...(params.bot && params.bot !== "all" ? { bot: params.bot } : {}),
      },
    }),
};

// DataForSEO's own external mention index for this Website's domain +
// its Competitors -- distinct from `reports.sources`, which reads the
// client's own completed scans. Doesn't cost tokens.
export const llmMentions = {
  get: (websiteId) => api.get(`/websites/${websiteId}/llm-mentions/`),
};

export const scans = {
  // Phase 3: live queue counts for the progress indicator. `pending` is
  // jobs waiting for a worker, `running` is currently being executed.
  queue: (websiteId) => api.get(`/websites/${websiteId}/scans/queue/`),
};

export const schedule = {
  get: (websiteId) => api.get(`/websites/${websiteId}/schedule/`),
  create: (websiteId, data) => api.post(`/websites/${websiteId}/schedule/`, data),
  update: (websiteId, data) =>
    api.patch(`/websites/${websiteId}/schedule/`, data),
  // Trigger the schedule's next run *now* rather than waiting for the
  // hourly dispatcher tick. Used by the "Run now" button when a
  // schedule is stuck in the past (previous tick skipped for low
  // tokens, user has since topped up).
  runNow: (websiteId) =>
    api.post(`/websites/${websiteId}/schedule/run-now/`, {}),
};

export const reports = {
  // Phase 2: `filters` accepts `{ start, end, ai_model }`. Omitted fields
  // fall back to the server's defaults (last 28 days, all models).
  dashboard: (websiteId, filters = {}) =>
    api.get(`/websites/${websiteId}/dashboard/`, {
      params: {
        ...(filters.start ? { start: filters.start } : {}),
        ...(filters.end ? { end: filters.end } : {}),
        ...(filters.ai_model && filters.ai_model !== "all"
          ? { ai_model: filters.ai_model }
          : {}),
      },
    }),
  answers: (websiteId, page = 1, pageSize = 25) =>
    api.get(`/websites/${websiteId}/answers/`, {
      params: { page, page_size: pageSize },
    }),
  sources: (websiteId) => api.get(`/websites/${websiteId}/sources/`),
};

export const billing = {
  packages: () => api.get("/tokens/packages/"),
  initiate: (packageId) =>
    api.post("/payments/initiate/", { package_id: packageId }),
};

// Phase 6: llms.txt generator. `description` is optional — when omitted
// the backend writes a niche-derived summary so the file is never blank.
// `probe=true` also fetches the user's site /llms.txt to detect whether
// one already exists; off by default to avoid hammering the origin on
// every page visit.
export const llmsTxt = {
  generate: (websiteId, { description, probe } = {}) => {
    const params = {};
    if (description) params.description = description;
    if (probe) params.probe = "1";
    return api.get(`/websites/${websiteId}/llms-txt/`, { params });
  },
};

// Phase 6: AI provider capability catalogue. Drives the "Soon" badge
// next to LLMs whose API key isn't configured yet.
export const aiProviders = {
  list: () => api.get("/ai/providers/"),
};

// Prompt Search Volume tab: real AI-search-volume data via DataForSEO,
// account-scoped (not tied to a specific Website). `lookup` bills the
// caller's token balance and appends to their own log.
export const promptVolume = {
  lookup: (keyword_text, region) =>
    api.post("/prompt-volume/lookup/", { keyword_text, region }),
  log: (page = 1) => api.get("/prompt-volume/log/", { params: { page } }),
};
